import { singleFrame } from "@/features/core/timers";
import { getState, setState } from "@/features/core/store";
import { clampReplayDurationMs } from "@/features/solver/replayDuration";

export type ReplayController = {
  // start a replay, or stop the one already playing (the Animate button is the same control for both)
  toggle: () => void;
  // stop any replay and put the fully solved path back on screen
  cancel: () => void;
};

// Owns the "Animate" replay: a RAF driver that maps elapsed wall-clock time onto the iterate
// polyline, so a replay takes the configured duration whether the solve produced 20 iterates or
// 100k. Each frame computes the head's fractional position straight from the clock, crossing
// however many iterates that takes; a per-step timer cannot deliver 100k ticks in a second.
export function createReplayController({
  isIterateHoverActive,
}: {
  // the user hovering a log row owns the highlight; the replay yields it
  isIterateHoverActive: () => boolean;
}): ReplayController {
  const frame = singleFrame();

  const cancel = () => {
    frame.cancel();
    const state = getState();
    if (!state.replayActive) return;
    // Stopping mid-sweep must leave exactly the picture a completed replay leaves: the whole path
    // back on screen (which also releases the scratch buffer) and the optimum star visible again.
    setState({
      iteratePath: state.originalIteratePath,
      iteratePhases: state.originalIteratePhases,
      replayActive: false,
      ...(isIterateHoverActive() ? {} : { highlightIteratePathIndex: null }),
    });
  };

  const start = () => {
    const state = getState();
    if (state.rotateObjectiveMode) return;
    const orig = state.originalIteratePath;
    const total = orig.count;
    const stride = orig.stride;
    if (total === 0) return;
    const origPhases = state.originalIteratePhases;
    const durationMs = clampReplayDurationMs(state.solverSettings.replayDurationMs);

    // One copy of the path per replay, never per frame: the sweep only ever rewrites the `stride`
    // floats (and the lift) of the moving head, so every point behind it is still the solver's own data.
    const points = orig.points.slice(0, total * stride);
    const lifts = orig.lift ? { shown: orig.lift.slice(0, total), original: orig.lift } : null;
    const lift = lifts?.shown ?? null;
    // which slot currently holds the interpolated head rather than its real
    // iterate, so it can be put back once the head has moved past it
    let headIndex = -1;
    let shownCount = 0;

    setState({
      iteratePath: { points, count: 0, stride, lift },
      iteratePhases: [],
      highlightIteratePathIndex: null,
      replayActive: true,
    });

    const startTime = performance.now();
    const tick = (timestamp: number) => {
      // whoever stopped the replay cleared the flag; a frame that was already
      // queued must not keep mutating the store or drawing after that
      if (!getState().replayActive) return;
      const progress = (timestamp - startTime) / durationMs;
      // cancel() restores the untouched original path, so the run ends exactly
      // on the last iterate rather than a rounded approximation of it
      if (progress >= 1 || total < 2) {
        cancel();
        return;
      }
      // the head's fractional position along the polyline: `base` whole
      // iterates plus `t` of the way into the segment leaving it
      const position = progress * (total - 1);
      const base = Math.floor(position);
      const t = position - base;
      const head = base + 1;
      const count = head + 1;

      if (headIndex >= 0 && headIndex !== head) {
        // the head has moved on, so that slot is a real iterate again
        const from = headIndex * stride;
        for (let k = 0; k < stride; k++) {
          points[from + k] = orig.points[from + k]!;
        }
        if (lifts) lifts.shown[headIndex] = lifts.original[headIndex]!;
      }
      const fromBase = base * stride;
      const headBase = head * stride;
      // every component interpolates, the lift included — leaving it at the
      // segment's end value would drag the head along the floor in 3D while the
      // rest of the path is lifted
      for (let k = 0; k < stride; k++) {
        const a = orig.points[fromBase + k]!;
        points[headBase + k] = a + t * (orig.points[headBase + k]! - a);
      }
      if (lifts) {
        const a = lifts.original[base]!;
        lifts.shown[head] = a + t * (lifts.original[head]! - a);
      }
      headIndex = head;

      setState({
        iteratePath: { points, count, stride, lift },
        // the phases have to cover the path (phasesCovering) or the iterate layers drop phase
        // colouring; they only change when the head crosses an iterate, so most frames skip the slice
        ...(origPhases.length > 0 && count !== shownCount ? { iteratePhases: origPhases.slice(0, count) } : {}),
        // The index tracks the last *whole* iterate the head has passed, for
        // everything that needs a real one (EllipsoidLayer reveals that
        // iterate's localizing ellipse). The green marker does not read it —
        // it rides the interpolated head instead, so it sweeps rather than
        // snapping — see createIterateHighlightLayer.
        ...(isIterateHoverActive() ? {} : { highlightIteratePathIndex: base }),
      });
      shownCount = count;
      // a listener of the patch above may have cancelled us mid-frame
      if (getState().replayActive) frame.request(tick);
    };
    frame.request(tick);
  };

  return {
    toggle: () => {
      if (getState().replayActive) cancel();
      else start();
    },
    cancel,
  };
}
